import React, { useState, useRef, useEffect } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { fileValidation } from '../helpers/filesValidation';
import ErrorModal from '../ui/ErrorModal';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/authContext';
import { useNavigate } from 'react-router-dom';

const AudioConverter = () => {
    // Initialize apiKey from sessionStorage if available, otherwise clear it
    const [apiKey, setApiKey] = useState(() => sessionStorage.getItem('elevenlabsApiKey') || '');
    // Initialize voiceId from sessionStorage if available, otherwise clear it
    const [voiceId, setVoiceId] = useState(() => sessionStorage.getItem('elevenlabsVoiceId') || '');
    const [textFiles, setTextFiles] = useState([]);
    const [voiceSettings, setVoiceSettings] = useState({
        speed: 1.09,
        stability: 0.54,
        similarityBoost: 0.75,
        style: null,
        useSpeakerBoost: true,
    });
    const [generationStatus, setGenerationStatus] = useState('');
    const fileInputRef = useRef(null);
    const generatedZips = useRef([]);
    const [modelId, setModelId] = useState('eleven_multilingual_v2');
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [processingFolder, setProcessingFolder] = useState('');
    const [processingStatus, setProcessingStatus] = useState('');
    const [isVoiceSettingsOpen, setVoiceSettingsOpen] = useState(false);
    const navigate = useNavigate();
    const { userLoggedIn } = useAuth();
    // State for "Save API Key" checkbox
    const [saveApiKeyForSession, setSaveApiKeyForSession] = useState(() =>
        !!sessionStorage.getItem('elevenlabsApiKey')
    );
    // New state for "Save Voice ID" checkbox
    const [saveVoiceIdForSession, setSaveVoiceIdForSession] = useState(() =>
        !!sessionStorage.getItem('elevenlabsVoiceId')
    );
    // State for batch size, default changed to 8
    const [batchSize, setBatchSize] = useState(8);

    useEffect(() => {
        console.log('userLoggedIn: ', userLoggedIn);
        if (!userLoggedIn) {
            navigate('/login');
        }
    }, [userLoggedIn, navigate]);

    // Effect to manage API key in sessionStorage
    useEffect(() => {
        if (saveApiKeyForSession) {
            sessionStorage.setItem('elevenlabsApiKey', apiKey);
        } else {
            sessionStorage.removeItem('elevenlabsApiKey');
        }
    }, [apiKey, saveApiKeyForSession]);

    // Effect to manage Voice ID in sessionStorage
    useEffect(() => {
        if (saveVoiceIdForSession) {
            sessionStorage.setItem('elevenlabsVoiceId', voiceId);
        } else {
            sessionStorage.removeItem('elevenlabsVoiceId');
        }
    }, [voiceId, saveVoiceIdForSession]);

    const handleVoiceIdChange = (event) => {
        setVoiceId(event.target.value);
    };

    const handleVoiceSettingsChange = (field, value) => {
        setVoiceSettings({ ...voiceSettings, [field]: value });
    };

    const handleModelIdChange = (event) => {
        setModelId(event.target.value);
    };

    const handleCloseErrorModal = () => {
        setIsErrorModalOpen(false);
        setValidationErrors([]);
        setTextFiles([]);
    };

    const handleFileChange = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            const folderName = files[0].webkitRelativePath.split('/')[0];
            setProcessingFolder(folderName);
        } else {
            setProcessingFolder('');
        }
        fileValidation(
            files,
            setTextFiles,
            setGenerationStatus,
            generatedZips,
            setValidationErrors,
            setIsErrorModalOpen
        );
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const generateAudio = async (text, audioName, currentVoiceSettings, fileName, lineNum) => {
        if (!apiKey) {
            setGenerationStatus(
                <p className="text-yellow-500 font-semibold">Please provide your ElevenLabs API Key.</p>
            );
            return null;
        }
        if (!voiceId) {
            setGenerationStatus(
                <p className="text-yellow-500 font-semibold">Please provide a Voice ID.</p>
            );
            return null;
        }

        const baseURL = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`;

        try {
            const statusMessage = `Generating audio for: "${fileName} : Line ${lineNum}"`;
            setGenerationStatus(<p className="text-blue-500 animate-pulse font-semibold">{statusMessage}</p>);

            const response = await fetch(baseURL, {
                method: 'POST',
                headers: {
                    'xi-api-key': apiKey,
                    'Content-Type': 'application/json',
                    'Accept': 'audio/mpeg',
                },
                body: JSON.stringify({
                    text: text,
                    model_id: modelId,
                    voice_settings: {
                        speed: currentVoiceSettings.speed,
                        stability: currentVoiceSettings.stability,
                        similarity_boost: currentVoiceSettings.similarityBoost,
                        style: currentVoiceSettings.style,
                        use_speaker_boost: currentVoiceSettings.useSpeakerBoost,
                    },
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, text: ${errorText}`);
            }

            const audioBlob = await response.blob();
            return audioBlob;
        } catch (error) {
            const errorMessage = `❌ Error generating audio for "${text}": ${error.message}`;
            console.error(errorMessage);
            setGenerationStatus(<p className="text-red-500 font-semibold">{errorMessage}</p>);
            setProcessingStatus(prevStatus => prevStatus + `${errorMessage}\n`);
            return null;
        }
    };

    const processFile = async (file) => {
        console.log('Processing file:', file.name);
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const content = e.target.result;

                const processedLines = content
                    .split('\n')
                    .filter(line => line.trim() !== '')
                    .map(line => line.replace(/^\d{2}:\d{2} - \d{2}:\d{2} - /, ''));
                const zip = new JSZip();
                let generationSuccess = true;

                const statusMessage = `Processing file: ${file.name} (${processedLines.length} lines)...`;
                setGenerationStatus(<p className="text-blue-500 font-semibold">{statusMessage}</p>);
                setProcessingStatus(prevStatus => prevStatus + `${statusMessage}\n`);
                console.log('Processed lines:', processedLines.length);

                for (let i = 0; i < processedLines.length; i++) {
                    const text = processedLines[i].trim();
                    const audioName = `edit${i + 1}.mp3`;
                    console.log('Generating audio for:', text, 'as', audioName);
                    const audioBlob = await generateAudio(text, audioName, voiceSettings, file.name, i);
                    if (audioBlob) {
                        console.log('Audio blob generated:', file.name, audioName);
                        zip.file(audioName, audioBlob);
                    } else {
                        generationSuccess = false;
                        break;
                    }
                    if (i < processedLines.length - 1) {
                        await new Promise(resolve => setTimeout(resolve, 1500));
                    }
                }

                if (generationSuccess) {
                    zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
                        generatedZips.current.push({ name: `${file.name.replace('.txt', '')}.zip`, blob: zipBlob });
                        const successMessage = `✅ Successfully processed and zipped: ${file.name}`;
                        setGenerationStatus(<p className="text-green-500 font-semibold">{successMessage}</p>);
                        setProcessingStatus(prevStatus => prevStatus + `${successMessage}\n`);
                        resolve(true);
                    });
                } else {
                    const failMessage = `❌ Failed to generate all audios for: ${file.name}. ZIP not created.`;
                    setGenerationStatus(<p className="text-red-500 font-semibold">{failMessage}</p>);
                    setProcessingStatus(prevStatus => prevStatus + `${failMessage}\n`);
                    resolve(false);
                }
            };
            reader.onerror = () => {
                const errorMessage = `❌ Error reading the file: ${file.name}`;
                setGenerationStatus(<p className="text-red-500 font-semibold">{errorMessage}</p>);
                setProcessingStatus(prevStatus => prevStatus + `${errorMessage}\n`);
                resolve(false);
            };
            reader.readAsText(file);
        });
    };

    const generateAllAndZip = async () => {
        if (!apiKey) {
            setGenerationStatus(
                <p className="text-yellow-500 font-semibold">Please provide your ElevenLabs API Key.</p>
            );
            return;
        }
        if (!textFiles.length) {
            setGenerationStatus(
                <p className="text-yellow-500 font-semibold">Please upload one or more text files.</p>
            );
            return;
        }

        const initialMessage = `Starting batch processing of ${textFiles.length} file(s) with batch size ${batchSize}...`;
        setGenerationStatus(<p className="text-blue-500 font-semibold">{initialMessage}</p>);
        setProcessingStatus(initialMessage + '\n');

        generatedZips.current = [];
        const batches = [];

        // Split files into batches
        for (let i = 0; i < textFiles.length; i += batchSize) {
            batches.push(textFiles.slice(i, i + batchSize));
        }

        for (const [index, batch] of batches.entries()) {
            const batchMessage = `Processing batch ${index + 1} of ${batches.length}...`;
            setGenerationStatus(<p className="text-blue-500 font-semibold">{batchMessage}</p>);
            setProcessingStatus((prevStatus) => prevStatus + `${batchMessage}\n`);

            // Process files in the current batch
            const batchResults = await Promise.all(batch.map((file) => processFile(file)));

            // Check if any file in the batch failed
            if (batchResults.includes(false)) {
                const errorMessage = `❌ Some files in batch ${index + 1} failed to process.`;
                setGenerationStatus(<p className="text-red-500 font-semibold">{errorMessage}</p>);
                setProcessingStatus((prevStatus) => prevStatus + `${errorMessage}\n`);
            }
        }

        if (generatedZips.current.length > 0) {
            const masterZip = new JSZip();
            generatedZips.current.forEach((zipData) => {
                masterZip.file(zipData.name, zipData.blob);
            });

            masterZip.generateAsync({ type: 'blob' }).then((masterZipBlob) => {
                saveAs(masterZipBlob, `${processingFolder || 'processed_audio'}_audio_files.zip`);
                const finalMessage = `✅ Successfully generated individual zips and a master zip!`;
                setGenerationStatus(<p className="text-green-500 font-semibold">{finalMessage}</p>);
                setProcessingStatus((prevStatus) => prevStatus + `${finalMessage}\n`);
            });
        } else {
            const noZipMessage = `❌ No ZIP files were generated.`;
            setGenerationStatus(<p className="text-red-500 font-semibold">{noZipMessage}</p>);
            setProcessingStatus((prevStatus) => prevStatus + `${noZipMessage}\n`);
        }
    };

    return (
        <div className="bg-gradient-to-br from-gray-900 to-indigo-900 to-black min-h-screen">
            <main className="pt-14">
                <div className="container mx-auto w-[800px] p-6 rounded-md shadow-md bg-white">
                    <div>
                        <div className="mb-2">
                            <label htmlFor="apiKey" className="block text-gray-700 text-sm font-bold mb-2">
                                ElevenLabs API Key:
                            </label>
                            <input
                                type="text"
                                id="apiKey"
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="Enter your ElevenLabs API key"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            />
                            <div className="mt-2 flex items-center">
                                <input
                                    type="checkbox"
                                    id="saveApiKey"
                                    checked={saveApiKeyForSession}
                                    onChange={(e) => setSaveApiKeyForSession(e.target.checked)}
                                    className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                />
                                <label htmlFor="saveApiKey" className="ml-2 block text-sm text-gray-900">
                                    Save API Key for this session
                                </label>
                            </div>
                        </div>

                        <div className="mb-2">
                            <label htmlFor="voiceId" className="block text-gray-700 text-sm font-bold mb-2">
                                Voice ID:
                            </label>
                            <input
                                type="text"
                                id="voiceId"
                                value={voiceId}
                                onChange={handleVoiceIdChange}
                                placeholder="Enter your ElevenLabs Artist VoiceID"
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            />
                            <div className="mt-2 flex items-center">
                                <input
                                    type="checkbox"
                                    id="saveVoiceId"
                                    checked={saveVoiceIdForSession}
                                    onChange={(e) => setSaveVoiceIdForSession(e.target.checked)}
                                    className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
                                />
                                <label htmlFor="saveVoiceId" className="ml-2 block text-sm text-gray-900">
                                    Save Voice ID for this session
                                </label>
                            </div>
                        </div>

                        <div className="mb-2 flex space-x-4"> {/* Use flex and space-x-4 for side-by-side */}
                            <div className="flex-1"> {/* Flex-1 to distribute space */}
                                <label htmlFor="modelId" className="block text-gray-700 text-sm font-bold mb-2">
                                    Model ID:
                                </label>
                                <select
                                    id="modelId"
                                    value={modelId}
                                    onChange={handleModelIdChange}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                >
                                    <option value="eleven_multilingual_v2">eleven_multilingual_v2</option>
                                    <option value="eleven_flash_v2_5">eleven_flash_v2_5</option>
                                </select>
                            </div>

                            <div className="flex-1"> {/* Flex-1 to distribute space */}
                                <label htmlFor="batchSize" className="block text-gray-700 text-sm font-bold mb-2">
                                    Batch Size:
                                </label>
                                <select
                                    id="batchSize"
                                    value={batchSize}
                                    onChange={(e) => setBatchSize(Number(e.target.value))}
                                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                >
                                    {[...Array(9).keys()].map(i => (
                                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="mb-2">
                            <label htmlFor="fileInput" className="block text-gray-700 text-sm font-bold mb-2">
                                Upload Files (Multiple .txt):
                            </label>
                            <input
                                type="file"
                                id="fileInput"
                                accept=".txt"
                                onChange={handleFileChange}
                                className="hidden"
                                ref={fileInputRef}
                                webkitdirectory="true"
                                mozdirectory="true"
                                msdirectory="true"
                                odirectory="true"
                                directory="true"
                                multiple
                            />
                            <button
                                onClick={triggerFileInput}
                                className="bg-indigo-600 text-white font-semibold py-[8px] px-[16px] rounded focus:outline-none focus:shadow-outline text-sm"
                            >
                                Browse
                            </button>
                            {textFiles.length > 0 && (
                                <p className="mt-2 text-sm text-gray-600">
                                    Selected <span className="font-semibold">{textFiles.length}</span> file(s).
                                </p>
                            )}
                        </div>

                        <div className="mb-2">
                            <div
                                className="flex items-center justify-between cursor-pointer"
                                onClick={() => setVoiceSettingsOpen(!isVoiceSettingsOpen)}
                            >
                                <h3 className="text-md font-bold text-gray-800">Voice Settings:</h3>
                                {isVoiceSettingsOpen ? (
                                    <ChevronUp className="h-8 w-8 text-indigo-700" />
                                ) : (
                                    <ChevronDown className="h-8 w-8 text-indigo-700" />
                                )}
                            </div>
                            {isVoiceSettingsOpen && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 bg-gray-100 p-2 rounded-md">
                                    <div>
                                        <label htmlFor="speed" className="block text-gray-700 text-sm font-bold mb-2">
                                            Speed:
                                        </label>
                                        <input
                                            type="number"
                                            id="speed"
                                            value={voiceSettings.speed}
                                            onChange={(e) => handleVoiceSettingsChange('speed', parseFloat(e.target.value))}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="stability" className="block text-gray-700 text-sm font-bold mb-2">
                                            Stability:
                                        </label>
                                        <input
                                            type="number"
                                            id="stability"
                                            value={voiceSettings.stability}
                                            onChange={(e) => handleVoiceSettingsChange('stability', parseFloat(e.target.value))}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="similarityBoost" className="block text-gray-700 text-sm font-bold mb-2">
                                            Similarity Boost:
                                        </label>
                                        <input
                                            type="number"
                                            id="similarityBoost"
                                            value={voiceSettings.similarityBoost}
                                            onChange={(e) =>
                                                handleVoiceSettingsChange('similarityBoost', parseFloat(e.target.value))
                                            }
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="style" className="block text-gray-700 text-sm font-bold mb-2">
                                            Style:
                                        </label>
                                        <input
                                            type="text"
                                            id="style"
                                            value={voiceSettings.style || ''}
                                            onChange={(e) => handleVoiceSettingsChange('style', e.target.value)}
                                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-1">
                                        <label
                                            htmlFor="useSpeakerBoost"
                                            className="inline-flex items-center text-gray-700 text-sm font-bold"
                                        >
                                            <input
                                                type="checkbox"
                                                id="useSpeakerBoost"
                                                checked={voiceSettings.useSpeakerBoost}
                                                onChange={(e) => handleVoiceSettingsChange('useSpeakerBoost', e.target.checked)}
                                                className="form-checkbox h-5 w-5 text-blue-500 focus:ring-blue-500 rounded border-gray-300"
                                            />
                                            <span className="ml-2">Use Speaker Boost</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={generateAllAndZip}
                            disabled={textFiles.length === 0 || !voiceId || !apiKey}
                            className={`bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${textFiles.length === 0 || !voiceId || !apiKey
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                                }`}
                        >
                            Process All Files and Generate Master ZIP
                        </button>

                        {generationStatus && <div className="mt-4">{generationStatus}</div>}
                        <div className="mt-4">
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Processing Status:
                            </label>
                            <textarea
                                value={processingStatus}
                                readOnly
                                rows={10}
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline font-mono text-sm bg-gray-50"
                            />
                        </div>
                    </div>
                </div>

                <ErrorModal
                    isOpen={isErrorModalOpen}
                    onClose={handleCloseErrorModal}
                    errorMessage={validationErrors}
                />
            </main>
        </div>
    );
};

export default AudioConverter;